<template>
  <section class="card">
    <header class="head">
      <h2>胸卡模板管理</h2>
      <button type="button" class="btn btn-primary" @click="openCreateDialog">新增模板</button>
    </header>
    <p class="note">
      请上传<strong>已设计好背景与 Logo 的 PNG 底图</strong>；系统仅在固定位置叠加<strong>姓名</strong>与<strong>职务（身份）</strong>。在活动编辑页勾选「启用胸卡」并选择模板后，签到成功可预览/打印，小程序端也可查看并保存。
    </p>

    <div class="filters filters-panel">
      <div class="flt">
        <input
          v-model.trim="keyword"
          class="flt-input flt-input--wide"
          placeholder="搜索模板名称"
          aria-label="搜索模板名称"
          @keyup.enter="applyFilters"
        />
      </div>
      <div class="flt">
        <FancySelect
          v-model="statusFilter"
          class="flt-select"
          :options="toolbarStatusOptions"
          placeholder="模板状态"
        />
      </div>
      <div class="filter-actions">
        <button type="button" class="btn btn-primary filter-action-btn" @click="applyFilters">查询</button>
        <button type="button" class="btn btn-cancel filter-action-btn" @click="resetFilters">重置</button>
      </div>
    </div>
    <p v-if="errorMsg" class="error">{{ errorMsg }}</p>

    <table v-if="rows.length">
      <thead>
        <tr>
          <th class="col-seq">序号</th>
          <th>模板名称</th>
          <th>版式</th>
          <th class="col-status">状态</th>
          <th>底图</th>
          <th class="col-center col-ops">操作</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(r, idx) in displayRows" :key="r.id">
          <td class="col-seq">{{ idx + 1 }}</td>
          <td>{{ r.templateName }}</td>
          <td>{{ layoutLabel(r.layoutType) }}</td>
          <td class="col-status">
            <span class="pill" :class="r.statusCode === 'ENABLED' ? 'pill-green' : 'pill-red'">
              {{ r.statusCode === 'ENABLED' ? '启用' : '停用' }}
            </span>
          </td>
          <td class="url-cell">
            <img v-if="r.imageUrl" class="thumb" :src="toFullUrl(r.imageUrl)" alt="" />
            <span v-else class="muted-inline">未上传</span>
          </td>
          <td class="col-center col-ops actions">
            <button type="button" class="btn btn-edit" @click="previewRow(r)">预览</button>
            <button type="button" class="btn btn-edit" @click="editRow(r)">编辑</button>
            <button type="button" class="btn btn-delete" @click="removeRow(r)">删除</button>
          </td>
        </tr>
      </tbody>
    </table>
    <div v-else class="empty-state">
      <p class="empty-title">暂无模板</p>
      <p class="empty-desc">先上传一个胸卡底图，活动编辑里即可选择并用于现场打印。</p>
      <button type="button" class="btn btn-primary" @click="openCreateDialog">立即新增模板</button>
    </div>

    <div v-if="showDialog" class="mask" @click.self="closeDialog">
      <div class="dialog dialog--shell dialog-wide">
        <div class="dialog-head">
          <h3 class="dialog-title">{{ form.id ? '编辑模板' : '新增模板' }}</h3>
          <button type="button" class="dialog-close" aria-label="关闭" @click="closeDialog">×</button>
        </div>
        <div class="dialog-body">
          <p v-if="dialogError" class="error dialog-error">{{ dialogError }}</p>
          <div class="form">
            <label class="field">
              <span>模板名称</span>
              <input v-model.trim="form.templateName" placeholder="例如：2026 年会横版胸卡" />
            </label>
            <label class="field">
              <span>版式</span>
              <FancySelect v-model="form.layoutType" :options="layoutOptions" placeholder="选择版式" />
            </label>
            <label class="field">
              <span>状态</span>
              <FancySelect v-model="form.statusCode" :options="formStatusOptions" placeholder="选择状态" />
            </label>
            <!-- 勿用 label 包裹编辑器：点击文字会触发标签聚焦并导致页面滚动 -->
            <div class="field field-full">
              <span>胸卡底图（PNG，含背景与 Logo）</span>
              <div class="file-line">
                <input :value="form.imageUrl" placeholder="请先上传底图" readonly />
                <button type="button" class="btn btn-ghost" @click="pickImage">
                  {{ form.imageUrl ? '更换图片' : '上传图片' }}
                </button>
              </div>
              <p v-if="form.imageUrl" class="file-hint" :title="form.imageUrl">{{ form.imageUrl }}</p>
              <BadgeTemplateEditor
                v-model="layoutDraft"
                class="field-editor"
                :image-url="form.imageUrl"
                :layout-type="form.layoutType"
                v-model:sample-name="editorSampleName"
                v-model:sample-identity="editorSampleIdentity"
              />
            </div>
          </div>
        </div>

        <div class="dialog-foot">
          <button type="button" class="btn btn-cancel" @click="closeDialog">取消</button>
          <button type="button" class="btn btn-primary" :disabled="saving" @click="saveTemplate">
            {{ saving ? '保存中...' : '保存' }}
          </button>
        </div>
      </div>
    </div>

    <div v-if="previewVisible" class="mask" @click.self="closePreview">
      <div class="dialog dialog--shell dialog-preview">
        <div class="dialog-head">
          <h3 class="dialog-title">胸卡预览</h3>
          <button type="button" class="dialog-close" aria-label="关闭" @click="closePreview">×</button>
        </div>
        <div class="dialog-body">
<div class="toolbar toolbar--preview">
          <input v-model.trim="previewSampleName" class="toolbar-input" placeholder="示例姓名" />
          <input v-model.trim="previewSampleIdentity" class="toolbar-input" placeholder="示例职务" />
          <button type="button" class="btn btn-edit" @click="reloadPreview">刷新预览</button>
          <button type="button" class="btn btn-primary" @click="exportWord">下载 Word（2 页）</button>
          <button type="button" class="btn btn-edit" @click="exportColorPng">下载 PNG 预览</button>
          <button type="button" class="btn btn-edit" @click="printPreview">浏览器打印</button>
        </div>
        <p class="print-note">
          推荐用「<strong>下载 Word（2 页）</strong>」：第 1 页正面、第 2 页反面，在 Word 里选<strong>双面打印 / 长边翻转</strong>即可印成一张卡的正反两面。
        </p>
        <p v-if="previewError" class="preview-error">{{ previewError }}</p>
        <div id="badge-print-area" class="preview-wrap preview-wrap--badge badge-print-target">
          <p v-if="previewLoading" class="preview-loading">加载预览中…</p>
          <ActivityBadgeCardPreview v-else :card="previewCardLive" :show-back="true" duplex-layout="stack" />
        </div>
        </div>

        <div class="dialog-foot">
          <button type="button" class="btn btn-primary" @click="closePreview">关闭</button>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { adminConfirm } from '@/utils/adminDialog'
import FancySelect from '@/components/FancySelect.vue'
import ActivityBadgeCardPreview from '@/components/ActivityBadgeCardPreview.vue'
import BadgeTemplateEditor from '@/components/BadgeTemplateEditor.vue'
import { defaultLayoutDraft, parseLayoutJson } from '@/utils/badgeTextSlotStyle.js'
import { exportBadgeColorPng, exportBadgeWord, printBadgeArea } from '@/utils/badgePrint.js'
import {
  BASE_URL,
  uploadImage,
  getBadgeTemplates,
  createBadgeTemplate,
  updateBadgeTemplate,
  deleteBadgeTemplate,
} from '@/api'

const emptyForm = () => ({
  id: 0,
  templateName: '',
  layoutType: 'LANDSCAPE',
  imageUrl: '',
  statusCode: 'ENABLED',
  remark: '',
})

export default {
  name: 'BadgeTemplateManageView',
  components: { FancySelect, ActivityBadgeCardPreview, BadgeTemplateEditor },
  data() {
    return {
      sourceRows: [],
      rows: [],
      keyword: '',
      statusFilter: '',
      toolbarStatusOptions: [
        { value: '', label: '全部状态' },
        { value: 'ENABLED', label: '启用' },
        { value: 'DISABLED', label: '停用' },
      ],
      formStatusOptions: [
        { value: 'ENABLED', label: '启用' },
        { value: 'DISABLED', label: '停用' },
      ],
      layoutOptions: [
        { value: 'LANDSCAPE', label: '横版' },
        { value: 'PORTRAIT', label: '竖版' },
        { value: 'SQUARE', label: '方形' },
      ],
      form: emptyForm(),
      layoutDraft: defaultLayoutDraft(),
      editorSampleName: '张三',
      editorSampleIdentity: '秘书长',
      saving: false,
      errorMsg: '',
      dialogError: '',
      showDialog: false,
      previewVisible: false,
      previewCardLive: null,
      previewRowData: null,
      previewLoading: false,
      previewError: '',
      previewSampleName: '张三',
      previewSampleIdentity: '秘书长',
    }
  },
  computed: {
    displayRows() {
      return (this.rows || []).slice().sort((a, b) => Number(a?.id || 0) - Number(b?.id || 0))
    },
  },
  async mounted() {
    await this.reload()
    this.tryOpenEditFromQuery()
  },
  methods: {
    layoutLabel(t) {
      const u = String(t || '').toUpperCase()
      if (u === 'PORTRAIT') return '竖版'
      if (u === 'SQUARE') return '方形'
      return '横版'
    },
    toFullUrl(url) {
      const raw = String(url || '').trim()
      if (!raw) return ''
      if (/^https?:\/\//i.test(raw)) return raw
      return `${BASE_URL}${raw.startsWith('/') ? '' : '/'}${raw}`
    },
    async reload() {
      try {
        this.errorMsg = ''
        this.sourceRows = (await getBadgeTemplates()) || []
        this.applyFilters()
      } catch (e) {
        this.errorMsg = e.message || '读取模板失败'
      }
    },
    tryOpenEditFromQuery() {
      const editId = Number(this.$route?.query?.editId)
      if (!Number.isFinite(editId) || editId <= 0) return
      const row = (this.sourceRows || []).find((r) => Number(r.id) === editId)
      if (row) this.editRow(row)
    },
    applyFilters() {
      const kw = String(this.keyword || '').trim().toLowerCase()
      const status = String(this.statusFilter || '').trim().toUpperCase()
      this.rows = (this.sourceRows || []).filter((x) => {
        const hitStatus = !status || String(x.statusCode || '').toUpperCase() === status
        if (!hitStatus) return false
        if (!kw) return true
        return String(x.templateName || '').toLowerCase().includes(kw)
      })
    },
    resetFilters() {
      this.keyword = ''
      this.statusFilter = ''
      this.applyFilters()
    },
    openCreateDialog() {
      this.resetForm()
      this.dialogError = ''
      this.showDialog = true
    },
    closeDialog() {
      this.showDialog = false
      this.dialogError = ''
    },
    resetForm() {
      this.form = emptyForm()
      this.layoutDraft = defaultLayoutDraft()
      this.editorSampleName = '张三'
      this.editorSampleIdentity = '秘书长'
      this.dialogError = ''
    },
    layoutJsonFromDraft() {
      return JSON.stringify(this.layoutDraft)
    },
    editRow(r) {
      this.form = {
        id: Number(r.id || 0),
        templateName: String(r.templateName || ''),
        layoutType: String(r.layoutType || 'LANDSCAPE'),
        imageUrl: String(r.imageUrl || ''),
        statusCode: String(r.statusCode || 'ENABLED'),
        remark: String(r.remark || ''),
      }
      this.layoutDraft = parseLayoutJson(r.layoutJson)
      this.editorSampleName = '张三'
      this.editorSampleIdentity = '秘书长'
      this.dialogError = ''
      this.showDialog = true
    },
    pickImage() {
      const input = document.createElement('input')
      input.type = 'file'
      input.accept = 'image/png,image/jpeg,image/webp'
      input.onchange = async () => {
        const file = input.files && input.files[0]
        if (!file) return
        try {
          this.errorMsg = ''
          const data = await uploadImage(file)
          const url = data.url || ''
          if (!url) throw new Error('未获取到图片地址')
          this.form.imageUrl = url
        } catch (e) {
          this.errorMsg = e.message || '图片上传失败'
        }
      }
      input.click()
    },
    async saveTemplate() {
      if (!String(this.form.templateName || '').trim()) {
        const msg = '请填写模板名称（弹窗最上方「模板名称」，不是预览区的张三）'
        this.dialogError = msg
        window.alert(msg)
        return
      }
      if (!String(this.form.imageUrl || '').trim()) {
        const msg = '请先上传胸卡底图'
        this.dialogError = msg
        window.alert(msg)
        return
      }
      this.saving = true
      this.dialogError = ''
      this.errorMsg = ''
      try {
        const payload = {
          templateName: this.form.templateName.trim(),
          layoutType: this.form.layoutType,
          imageUrl: this.form.imageUrl.trim(),
          layoutJson: this.layoutJsonFromDraft(),
          statusCode: this.form.statusCode,
          remark: this.form.remark || '',
        }
        if (this.form.id) {
          await updateBadgeTemplate(this.form.id, payload)
        } else {
          await createBadgeTemplate(payload)
        }
        window.alert('保存成功')
        this.closeDialog()
        await this.reload()
      } catch (e) {
        const msg = e.message || '保存失败'
        this.dialogError = msg
        window.alert(msg)
      } finally {
        this.saving = false
      }
    },
    async removeRow(r) {
      if (!await adminConfirm(`确定删除模板「${r.templateName}」？`)) return
      try {
        await deleteBadgeTemplate(r.id)
        await this.reload()
      } catch (e) {
        this.errorMsg = e.message || '删除失败'
      }
    },
    buildPreviewCardFromRow(r) {
      if (!r) {
        return { available: false, message: '未选择模板' }
      }
      const imageUrl = String(r.imageUrl || '').trim()
      if (!imageUrl) {
        return { available: false, message: '该模板尚未上传底图，请先编辑模板并上传 PNG' }
      }
      const layout = parseLayoutJson(r.layoutJson)
      return {
        available: true,
        layoutType: r.layoutType || 'LANDSCAPE',
        imageUrl,
        displayName: this.previewSampleName || '张三',
        identityTitle: this.previewSampleIdentity || '秘书长',
        nameSlot: { ...layout.name },
        identitySlot: { ...layout.identity },
      }
    },
    async previewRow(r) {
      this.previewRowData = r
      this.previewSampleName = '张三'
      this.previewSampleIdentity = '秘书长'
      this.previewError = ''
      this.previewVisible = true
      await this.reloadPreview()
    },
    closePreview() {
      this.previewVisible = false
      this.previewCardLive = null
      this.previewRowData = null
      this.previewError = ''
      this.previewLoading = false
    },
    async reloadPreview() {
      if (!this.previewRowData) return
      this.previewLoading = true
      this.previewError = ''
      try {
        this.previewCardLive = this.buildPreviewCardFromRow(this.previewRowData)
        if (!this.previewCardLive.available) {
          this.previewError = this.previewCardLive.message || '无法预览'
        }
      } catch (e) {
        this.previewCardLive = { available: false, message: e.message || '预览失败' }
        this.previewError = e.message || '预览失败'
      } finally {
        this.previewLoading = false
      }
    },
    async exportWord() {
      if (!this.previewCardLive || !this.previewCardLive.available) {
        this.previewError = '请先确保预览正常显示后再导出'
        return
      }
      try {
        await exportBadgeWord(this.previewCardLive)
      } catch (e) {
        this.previewError = e.message || '导出 Word 失败'
      }
    },
    async exportColorPng() {
      if (!this.previewCardLive || !this.previewCardLive.available) {
        this.previewError = '请先确保预览正常显示后再导出'
        return
      }
      await exportBadgeColorPng(this.previewCardLive)
    },
    printPreview() {
      if (!this.previewCardLive || !this.previewCardLive.available) {
        this.previewError = '请先确保预览正常显示后再打印'
        return
      }
      printBadgeArea('badge-print-area', this.previewCardLive)
    },
  },
}
</script>

<style scoped>
/* 与 ConfirmTemplateManageView / FeeTemplateManageView 一致 */
.card {
  background: #fff;
  border-radius: 12px;
  padding: 16px;
}
.head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}
.head h2 {
  margin: 0;
  font-size: 18px;
  color: #101828;
}
.note {
  margin: 0 0 12px;
  background: #f5f8ff;
  border: 1px solid #dae5ff;
  color: #2a4b8d;
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 12px;
  line-height: 1.5;
}
.note strong {
  font-weight: 600;
}
.filters .flt-input {
  width: 180px;
  height: 40px;
  box-sizing: border-box;
  border: 1px solid #d0d7e2;
  border-radius: 8px;
  padding: 0 10px;
  font-size: 13px;
  background: #fff;
}
.filters .flt-input--wide {
  width: 280px;
}
.filters .flt-select {
  width: 170px;
}
:deep(.filters .fs-btn) {
  height: 40px;
  min-width: 170px;
  border-radius: 8px;
}
.toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
}
.toolbar--preview {
  margin-bottom: 12px;
}
.toolbar-input {
  min-width: 160px;
  width: 200px;
  flex-shrink: 0;
  height: 40px;
  box-sizing: border-box;
  border: 1px solid #d0d7e2;
  border-radius: 8px;
  padding: 0 10px;
  background: #fff;
}
table {
  width: 100%;
  border-collapse: collapse;
}
th,
td {
  border-bottom: 1px solid #edf0f5;
  padding: 8px;
  font-size: 13px;
  text-align: left;
  vertical-align: middle;
}
.col-seq,
.col-status {
  text-align: center;
}
.url-cell {
  max-width: 120px;
}
.thumb {
  display: block;
  width: 88px;
  height: 52px;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid #e4e7ec;
  background: #f8fafc;
}
.muted-inline {
  font-size: 12px;
  color: #98a2b3;
}
.btn {
  border-radius: 8px;
  padding: 6px 12px;
  cursor: pointer;
  border: 1px solid transparent;
  font-size: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  line-height: 1;
}
.btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}
td.actions .btn + .btn {
  margin-left: 8px;
}
.btn-primary {
  background: #2f6af6;
  color: #fff;
}
.btn-edit {
  border-color: #bfc9db;
  background: #fff;
  color: #26457d;
}
.btn-cancel {
  border-color: #d0d7e2;
  background: #fff;
  color: #344054;
}
.btn-ghost {
  color: #2f6af6;
  background: #eef4ff;
  border-color: #d7e4ff;
}
.btn-delete {
  border-color: #ffc8c2;
  background: #fff1f0;
  color: #d92d20;
}
.error {
  color: #d92d20;
  margin: 0 0 8px;
  font-size: 13px;
}
.pill {
  display: inline-block;
  min-width: 58px;
  text-align: center;
  padding: 3px 8px;
  border-radius: 999px;
  font-size: 12px;
  line-height: 18px;
  font-weight: 600;
}
.pill-green {
  color: #067647;
  background: #ecfdf3;
  border: 1px solid #abefc6;
}
.pill-red {
  color: #b42318;
  background: #fef3f2;
  border: 1px solid #fecdca;
}
.file-hint {
  margin: 8px 0 0;
  color: #475467;
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}
.dialog .dialog .actions .btn + .btn {
  margin-left: 0;
}
.empty-state {
  border: 1px dashed #cfd8ee;
  border-radius: 12px;
  background: #fbfcff;
  padding: 36px 20px;
  text-align: center;
}
.empty-title {
  margin: 0;
  font-size: 18px;
  color: #1d2939;
}
.empty-desc {
  margin: 8px 0 16px;
  color: #667085;
  font-size: 14px;
}
.dialog {
  width: 760px;
  max-width: calc(100vw - 40px);
  max-height: calc(100vh - 40px);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 12px;
  padding: 16px;
  box-sizing: border-box;
}
.dialog-wide {
  width: 960px;
}
.dialog-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
}
.dialog-foot {
  flex: 0 0 auto;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding-top: 12px;
  border-top: 1px solid #edf0f5;
  margin-top: 12px;
  background: #fff;
}
.dialog-error {
  margin-bottom: 10px;
}
.field-editor {
  display: block;
  width: 100%;
}
.dialog h3 {
  margin: 0 0 12px;
  font-size: 16px;
  color: #101828;
}
.form {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}
.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 13px;
  color: #344054;
}
.field > span {
  font-weight: 600;
}
.field-full {
  grid-column: 1 / -1;
}
.file-line {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 10px;
  align-items: center;
}
.form input {
  border: 1px solid #d0d7e2;
  border-radius: 8px;
  padding: 8px 10px;
  background: #fff;
  box-sizing: border-box;
  width: 100%;
}
:deep(.form .fs-btn) {
  height: 40px;
  border-radius: 8px;
}
.dialog-preview {
  width: 980px;
  max-width: calc(100vw - 40px);
}
.preview-wrap {
  border: 1px solid #e4eaf6;
  border-radius: 10px;
  background: #fff;
  overflow: auto;
}
.print-note {
  margin: 0 0 10px;
  padding: 8px 10px;
  font-size: 12px;
  line-height: 1.5;
  color: #475467;
  background: #f8fafc;
  border: 1px solid #e4e7ec;
  border-radius: 8px;
}
.print-note strong {
  font-weight: 600;
  color: #344054;
}
.preview-error {
  margin: 0 0 8px;
  color: #d92d20;
  font-size: 13px;
}
.preview-loading {
  margin: 0;
  color: #667085;
  font-size: 14px;
}
.preview-wrap--badge {
  min-height: 280px;
  max-height: 68vh;
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.preview-wrap--badge :deep(.badge-wrap) {
  width: min(520px, 100%);
}
</style>
